import { useState } from 'react';
import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import CanonicalTag from '@/components/CanonicalTag';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Calendar, Home, DollarSign, Building, HelpCircle, Heart, Shield, Users, Accessibility } from 'lucide-react';

const HowToUseADUAsAccessibleRetirementSuite = () => {
  const [showCalendly, setShowCalendly] = useState(false);

  const ctaButtons = [
    {
      icon: Calendar,
      text: "Book & Schedule",
      onClick: () => setShowCalendly(true)
    },
    {
      icon: Building,
      text: "What Fits My Lot?",
      onClick: () => window.open('/landing', '_self')
    },
    {
      icon: DollarSign,
      text: "Get Price & ROI Report",
      onClick: () => window.open('/adu-calculator', '_self')
    },
    {
      icon: Home,
      text: "Our Portfolio",
      onClick: () => window.open('/our-work', '_self')
    },
    {
      icon: HelpCircle,
      text: "Our FAQ",
      onClick: () => window.open('/faq', '_self')
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <CanonicalTag url="https://www.udaadu.com/how-to-use-adu-as-accessible-retirement-suite" />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-20 pb-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: '#E4683B' }}>
            How to Use Your ADU as a Fully Accessible Retirement Suite
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            ADUs designed for accessibility offer dignity, safety, and proximity to loved ones for seniors or those with disabilities.
          </p>
          
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: 'white' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Independent Living Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>Independent Living Without Leaving the Property</h2>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Accessibility className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Step-free entry</span>
                </div>
                <div className="flex items-center gap-3">
                  <Home className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Wide doorways (36"+)</span>
                </div>
                <div className="flex items-center gap-3">
                  <Shield className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Grab bars, lever handles</span>
                </div>
                <div className="flex items-center gap-3">
                  <Heart className="w-5 h-5" style={{ color: '#E4683B' }} />
                  <span className="text-white">Walk-in roll-in shower</span>
                </div>
              </div>
            </div>
            <div className="bg-muted rounded-lg p-8">
              <img 
                src="https://images.unsplash.com/photo-1631217868264-e5b90bb7e133?auto=format&fit=crop&w=800&h=400" 
                alt="Accessible Retirement Suite ADU" 
                className="w-full h-64 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Universal Design Features Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Universal Design Features</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Accessibility Standards</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• ADA-compliant ramps and entries</li>
                <li>• 36" minimum doorway widths</li>
                <li>• Lever-style door handles and faucets</li>
              </ul>
            </div>
            <div className="space-y-4">
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Safety Features</h3>
              <ul className="space-y-2 text-muted-foreground">
                <li>• Grab bars in bathroom and shower areas</li>
                <li>• Non-slip flooring throughout</li>
                <li>• Emergency communication systems</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Health Support Upgrades Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-8" style={{ color: '#E4683B' }}>Health Support Upgrades</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Emergency Systems</h3>
              <p className="text-muted-foreground">Emergency call system integration</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Home className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>ADA Appliances</h3>
              <p className="text-muted-foreground">ADA-height appliances and countertops</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Accessibility className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Safe Surfaces</h3>
              <p className="text-muted-foreground">Anti-slip surfaces and plenty of light</p>
            </div>
          </div>
        </div>
      </section>

      {/* Financial and Emotional ROI Section */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold mb-8 text-center" style={{ color: '#E4683B' }}>Financial and Emotional ROI</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <DollarSign className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Cost Savings</h3>
              <p className="text-muted-foreground">Delays nursing home transitions</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Users className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Family Flexibility</h3>
              <p className="text-muted-foreground">Increases multigenerational flexibility</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Heart className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Peace of Mind</h3>
              <p className="text-muted-foreground">Preserves privacy with peace of mind</p>
            </div>
          </div>
        </div>
      </section>

      {/* Design Considerations Section */}
      <section className="py-16 px-4 bg-secondary/50">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold mb-12 text-center" style={{ color: '#E4683B' }}>Essential Design Considerations</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Accessibility className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Mobility Access</h3>
              <p className="text-muted-foreground">Zero-step entries and wide hallways for wheelchairs</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Shield className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Safety First</h3>
              <p className="text-muted-foreground">Emergency systems and fall prevention features</p>
            </div>
            <div className="text-center space-y-4">
              <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                <Heart className="w-8 h-8" style={{ color: '#E4683B' }} />
              </div>
              <h3 className="text-xl font-semibold" style={{ color: '#E4683B' }}>Comfort & Dignity</h3>
              <p className="text-muted-foreground">Maintaining independence while staying close to family</p>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-6" style={{ color: '#E4683B' }}>
            Create a Dignified Living Space
          </h2>
          <p className="text-xl text-muted-foreground mb-8">
            Design an accessible ADU that supports independence while keeping loved ones close.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            {ctaButtons.map((button, index) => (
              <Button
                key={index}
                onClick={button.onClick}
                className="flex items-center gap-2"
                style={{ backgroundColor: index === 0 ? '#E4683B' : 'transparent', borderColor: '#E4683B', color: 'white' }}
                variant={index === 0 ? "default" : "outline"}
              >
                <button.icon className="w-4 h-4" />
                {button.text}
              </Button>
            ))}
          </div>
        </div>
      </section>

      <Footer />

      {/* Calendly Modal */}
      <Dialog open={showCalendly} onOpenChange={setShowCalendly}>
        <DialogContent className="max-w-4xl w-full h-[600px] p-0">
          <div className="w-full h-full">
            <iframe
              src="https://calendly.com/udaadu"
              width="100%"
              height="100%"
              frameBorder="0"
              title="Schedule a consultation"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HowToUseADUAsAccessibleRetirementSuite;